import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";

import ModalTemplate from "../../../../../components/modal/modalTemplate";
import {
    certificatesAndDocumentApprove,
    deleteCertificate,
    fetchCertificates,
    uploadDocuments,
} from "../../../service";
import NotificationMessages from "../../NotificationMessages";
import { commonSwal } from "../../../../../components/swalAlert/swalAlert";
import { theme } from "../../../../../theme";
import DivSpinner from "../../../../../components/divSpinner";
import { formatTimestampInMap } from "../../../../../utils";
import SetExpiryDate from "../../SetExpiryDate";
import styles from "../../styles";
import CheckLists from "../../CheckLists";
import FilesView from "../../filesView/FilesView";
import BtnSpinner from "../../../../../components/btnSpinner";

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];

// 🔹 Small presentational components
const FileStatus = ({ status, type }) => {
    const Icon = ({ icon, className }) => (
        <i className={`material-icons ${className} text-md`}>{icon}</i>
    );

    const statusMap = {
        EXISTING: { icon: "verified", text: "UPLOADED", class: "text-success" },
        uploading: { icon: "upload", text: "UPLOADING...", class: "text-warning" },
        success: { icon: "verified", text: "UPLOADED", class: "text-success" },
        error: { icon: "error", text: "ERROR", class: "text-danger" },
    };

    const current = statusMap[type === "EXISTING" ? "EXISTING" : status];
    if (!current) return null;

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Icon icon={current.icon} className={current.class} />
            <span className="font-weight-bold text-dark text-xs">{current.text}</span>
        </div>
    );
};

const FtmDocuments = ({
    show,
    handleClose,
    selectedDocumentType,
    selectedDocumentTypeType,
    documentTypes,
    parentRefresh,
    handleNextItem,
    handlePrevItem,
}) => {
    const { ftm_id } = useParams();

    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [certificateDocumentId, setCertificateDocumentId] = useState(null);
    const [isApproved, setIsApproved] = useState(false);
    const [documentTypesIsExpiryDateMandatory, setDocumentTypesIsExpiryDateMandatory] = useState(false);
    const [approveDocumentDetails, setApproveDocumentDetails] = useState(null);
    const [checkLists, setCheckLists] = useState(null);
    const [acceptCheckList, setAcceptCheckList] = useState(false);

    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileViewId, setFileViewId] = useState(0);

    const [fileUploading, setFileUploading] = useState(false);

    const expiryRef = useRef();

    const handleFileViewOpen = (id) => {
        setFileViewId(id);
        setShowFileViewModal(true);
    }

    const handleFileViewClose = () => {
        setShowFileViewModal(false);
    }

    const index = documentTypes?.findIndex(
        (t) => t.documents_types_id === selectedDocumentType?.documents_types_id
    );
    const title = `Upload (${selectedDocumentTypeType}) ${selectedDocumentType?.documents_types_name} (${index + 1} of ${documentTypes.length})`;

    const nav = useMemo(
        () => ({
            prev: index > 0 ? documentTypes[index - 1] : null,
            next: index < documentTypes.length - 1 ? documentTypes[index + 1] : null,
        }),
        [index, documentTypes]
    );

    // 🔹 Fetch documents
    useEffect(() => {
        if (!selectedDocumentType) return;

        const loadDocuments = async () => {
            setLoading(true);
            try {
                const response = await fetchCertificates({
                    ftm_id,
                    documents_types_id: selectedDocumentType?.documents_types_id
                });

                if (response?.status === "Success") {
                    const newDocs =
                        response?.documents?.ftm_document_files?.map((doc, i) => {
                            const fileName = doc.ftm_document_file_path.split("/").pop();
                            const isImage = IMAGE_EXTENSIONS.some((ext) =>
                                fileName.toLowerCase().endsWith(ext)
                            );

                            return {
                                id: `${Date.now()}-${i}`,
                                file: null,
                                preview: doc.ftm_document_file_path,
                                fileName,
                                isImage,
                                status: "success",
                                ftm_document_file_id: doc.ftm_document_file_id,
                                type: "EXISTING",
                                date_time_uploaded: doc.ftm_document_file_uploaded_at
                            };
                        }) || [];

                    setFiles(newDocs);
                    setCertificateDocumentId(response?.documents?.ftm_document_id);
                    setIsApproved(response?.documents?.ftm_document_is_approve === 1);
                    setDocumentTypesIsExpiryDateMandatory(response?.documents?.documents_type?.documents_types_is_expiry_date_mandatory);
                    setCheckLists(response?.documents?.documents_type?.document_type_check_list);
                    setApproveDocumentDetails(response?.documents?.document_approve);
                } else {
                    setFiles([]);
                }
            } catch (error) {
                console.error("Error fetching documents:", error);
                setFiles([]);
            } finally {
                setLoading(false);
            }
        };

        loadDocuments();
    }, [ftm_id, selectedDocumentType]);

    const hasUploadingFiles = useMemo(
        () => files.some((f) => f.status === "uploading" || f.status === "pending"),
        [files]
    );

    const updateFileStatus = useCallback((id, updates) => {
        setFiles((prev) =>
            prev.map((f) => (f.id === id ? { ...f, ...updates } : f))
        );
    }, []);

    const uploadFile = useCallback(
        async (fileObj) => {
            updateFileStatus(fileObj.id, { status: "uploading" });

            try {
                const response = await uploadDocuments({
                    ftm_id,
                    ftm_document_type_id: selectedDocumentType?.documents_types_id,
                    ftm_document_type: selectedDocumentType?.documents_types_name,
                    document_type: selectedDocumentType?.documents_types_type,
                    ftm_document: fileObj.file,
                });

                updateFileStatus(fileObj.id, {
                    status: response?.status === "Success" ? "success" : "error",
                    ...(response?.status === "Success" && {
                        ftm_document_file_id: response.ftm_document_file_id,
                        document_path: response.document_path,
                    }),
                });

                setCertificateDocumentId(response?.ftm_document_id);
            } catch {
                updateFileStatus(fileObj.id, { status: "error" });
            }
        },
        [ftm_id, selectedDocumentType, updateFileStatus]
    );

    // ✅ Sequential upload helper
    const uploadFilesSequentially = useCallback(
        async (filesToUpload) => {
            setFileUploading(true);
            for (const fileObj of filesToUpload) {
                await uploadFile(fileObj); // wait until the current file finishes before next
            }

            setFileUploading(false);
        },
        [uploadFile]
    );

    const onDrop = useCallback(
        (acceptedFiles) => {
            // create file objects first
            const newFileObjs = acceptedFiles.map((file, i) => {
                const id = `${Date.now()}-${i}`;
                const isImage = IMAGE_EXTENSIONS.some((ext) =>
                    file.name.toLowerCase().endsWith(ext)
                );

                return {
                    id,
                    file,
                    preview: URL.createObjectURL(file),
                    fileName: file.name,
                    isImage,
                    status: "pending",
                    type: "NEW",
                    date_time_uploaded: new Date().getTime(),
                };
            });

            // show them in UI immediately
            setFiles((prev) => [...newFileObjs, ...prev]);

            // upload one by one
            uploadFilesSequentially(newFileObjs);
        },
        [uploadFilesSequentially]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [], "application/pdf": [] },
        multiple: true,
    });

    const removeFile = useCallback(
        (file) => {
            const { id, status, ftm_document_file_id } = file;

            if (status === "error") {
                setFiles((prev) => prev.filter((f) => f.id !== id));
                return;
            }

            commonSwal({
                notificationType: "UPLOAD_IMAGE_DELETE",
                preConfirm: () => {
                    updateFileStatus(id, { status: "uploading" });
                    return deleteCertificate(ftm_document_file_id);
                },
                isConfirmed: (result) => {
                    if (result?.isConfirmed && result?.value?.status === "Success") {
                        setFiles((prev) => prev.filter((f) => f.id !== id));
                    } else {
                        updateFileStatus(id, { status: "success" }); // rollback
                    }
                },
            });
        },
        [updateFileStatus]
    );

    const documentApprove = async () => {
        try {
            commonSwal({
                notificationType: "APPROVE_FTM_DOCUMENT",
                preConfirm: async () => {
                    const saved = await expiryRef.current?.saveExpiryData();
                    if (saved === false) return;

                    return await certificatesAndDocumentApprove({
                        document_id: certificateDocumentId,
                        document_type: "certificate",
                    })
                },
                isConfirmed: (result) => {
                    if (result.isConfirmed) {
                        const { status, message } = result?.value ?? {};
                        if (status === "Success") {
                            setIsApproved(true);
                            parentRefresh();
                            toast.success(message);
                        } else {
                            message && toast.error(message);
                        }
                    }
                },
            });
        } catch (err) {
            console.error("Approve error:", err);
        }
    };

    return (
        <ModalTemplate
            show={show}
            closeFun={handleClose}
            title={title}
            size={"lg"}
            fullscreen={false}
            centered={true}
            dialogClassName={"modal-900w"}
            body={
                <section>
                    <div className="row">
                        {loading ? (
                            <div style={{ height: `${window.innerHeight - 387}px` }}>
                                <DivSpinner />
                            </div>
                        ) : (
                            <>
                                <div className="col-7">
                                    {isApproved && (
                                        <NotificationMessages
                                            color={theme.main}
                                            override={`${selectedDocumentType?.documents_types_name} document approved`}
                                        />
                                    )}

                                    {!isApproved && (
                                        <div className="mt-3" {...getRootProps({ style: styles.dropzone })}>
                                            <input {...getInputProps()} />
                                            <p className="font-weight-bold mt-3">
                                                {isDragActive
                                                    ? "Drop the files here..."
                                                    : "Drag & drop files or click to select"}
                                            </p>
                                        </div>
                                    )}

                                    {files.length ? (
                                        <div>
                                            <div
                                                style={{
                                                    ...styles.thumbsContainer,
                                                    justifyContent: window.innerWidth < 770 ? "center" : ""
                                                }}
                                            >
                                                {files.map((file) => (
                                                    <div key={file.id} style={styles.thumb} onClick={() => handleFileViewOpen(file.id)} className="cursor-pointer">
                                                        {(file.status === "success" || file.status === "error") && (
                                                            <button
                                                                onClick={() => removeFile(file)}
                                                                style={styles.topRightRemoveBtn}
                                                                title="Remove"
                                                            >
                                                                &times;
                                                            </button>
                                                        )}

                                                        {file.isImage ? (
                                                            <img
                                                                src={file.preview}
                                                                alt={file.fileName}
                                                                style={styles.thumbImage}
                                                            />
                                                        ) : (
                                                            <div style={styles.pdfPreview}>
                                                                <i
                                                                    className="material-icons"
                                                                    style={{ fontSize: "48px" }}
                                                                >
                                                                    file_present
                                                                </i>
                                                            </div>
                                                        )}

                                                        <p className="mb-1 font-weight-bold text-dark" style={styles.fileName}>
                                                            {file?.date_time_uploaded ? formatTimestampInMap(file.date_time_uploaded, true) : "N/A"}
                                                        </p>
                                                        <p className="font-weight-bold mb-0 pb-0" style={styles.status}>
                                                            <FileStatus status={file.status} type={file.type} />
                                                        </p>
                                                    </div>
                                                ))}

                                                <FilesView
                                                    show={showFileViewModal}
                                                    handleClose={handleFileViewClose}
                                                    title={`${selectedDocumentType?.documents_types_name} (${selectedDocumentTypeType})`}
                                                    files={files}
                                                    selectedId={fileViewId}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={styles.emptyState}>
                                            <i
                                                className="material-icons"
                                                style={{ fontSize: "64px", color: "#ccc" }}
                                            >
                                                folder_open
                                            </i>
                                            <p style={{ fontSize: "14px", color: "#777" }}>
                                                No files uploaded yet
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="col-5">
                                    {checkLists?.document_type_check_items.length > 0 && (
                                      <CheckLists  
                                            list={checkLists}
                                            accepted={acceptCheckList}
                                            handleAccepted={setAcceptCheckList}
                                            isApprove={isApproved}
                                            approveDocumentDetails={approveDocumentDetails}
                                            type="certificate"
                                        />
                                    )}

                                    <div className="mt-4">
                                        <SetExpiryDate
                                            ref={expiryRef}
                                            document_id={certificateDocumentId}
                                            document_type="certificate"
                                            document_types_is_expiry_date_mandatory={documentTypesIsExpiryDateMandatory === 1}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </section>
            }
            footer={
                <div className="d-flex w-100 align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                        {nav.prev && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-dark d-flex align-items-center ms-2"
                                onClick={() => !hasUploadingFiles && handlePrevItem(selectedDocumentType?.documents_types_id)}
                                disabled={fileUploading}
                            >
                                {fileUploading ? <BtnSpinner/> : (
                                    <>
                                        <i className="material-icons me-1" style={{ fontSize: "15px" }}>arrow_back</i>
                                        <span>Back</span>
                                    </>
                                )}
                            </button>
                        )}

                        {nav.next ? (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-dark d-flex align-items-center ms-2"
                                onClick={() => {
                                    if (!hasUploadingFiles) {
                                        nav.next
                                            ? handleNextItem(selectedDocumentType?.documents_types_id)
                                            : handleClose();
                                    }
                                }}
                                disabled={fileUploading}
                            >
                                {fileUploading ? <BtnSpinner/> : (
                                    <>
                                        <span>Next</span>
                                        <i className="material-icons ms-1" style={{ fontSize: "15px" }}>arrow_forward</i>
                                    </>
                                )}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-dark d-flex align-items-center ms-2"
                                onClick={handleClose}
                                disabled={fileUploading}
                            >
                                {fileUploading ? <BtnSpinner/> : (
                                    <>
                                        <span>Finish</span>
                                        <i className="material-icons ms-1" style={{ fontSize: "15px" }}>done_all</i>
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    <div className="ms-auto">
                        <button
                            className={`btn btn-${theme.main} btn-sm d-flex align-items-center`}
                            onClick={documentApprove}
                            disabled={(checkLists?.document_type_check_items.length > 0 ? !acceptCheckList : false) || isApproved || fileUploading}
                        >
                            <i className="material-icons me-1" style={{ fontSize: "15px" }}>check</i>
                            <span>Approve</span>
                        </button>
                    </div>
                </div>
            }
        />
    );
};

export default FtmDocuments;
